import React, { useState, useEffect, useContext } from 'react';
import { userAPI, vehicleAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'react-toastify';
import PageTitle from '../shared/PageTitle'; // Assuming PageTitle is used and imported
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const Users = () => {
  // Get current user for access control
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // For user form
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'owner',
    committee_role: '',
    organization_id: null,
    id: null
  });
  
  // Add new state for form errors
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: '',
    committee_role: ''
  });
  
  // For user profile view
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [userVehicles, setUserVehicles] = useState([]);
  
  // For delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // For organization context
  const [currentUserOrg, setCurrentUserOrg] = useState(null);
  
  // For phone error
  // const [phoneError, setPhoneError] = useState('');

  // For search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // You can adjust this number

  // Pagination calculations (based on filteredUsers)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Pagination actions
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('users-table-section');
    }, 50);
  };
  const previousPage = () => { 
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('users-table-section');
      }, 50);
    }
  };
  const nextPage = () => { 
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('users-table-section');
      }, 50);
    }
  };

  // Fetch users and get organization context
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        // Get current user's organization from session storage
        const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        const orgId = currentUser.organization_id || null;
        setCurrentUserOrg(orgId);
        
        // Set organization_id in the form data
        setFormData(prev => ({
          ...prev,
          organization_id: orgId
        }));
        
        const response = await userAPI.getAllUsers();
        setUsers(response.data);
        // Initial filtering after fetching users
        setFilteredUsers(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, []);

  // Effect to filter users and reset page when search term or users change
  useEffect(() => {
    let results = [...users];
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      results = results.filter(user =>
        (user.name && user.name.toLowerCase().includes(search)) ||
        (user.email && user.email.toLowerCase().includes(search)) ||
        (user.phone && user.phone.toLowerCase().includes(search))
      );
    }
    setFilteredUsers(results);
    setCurrentPage(1); // Reset to first page on filter change
  }, [users, searchTerm]);

  // Generate a unique CSS class name based on the current settings
  const themeClassName = `theme-header-${Date.now()}`;
  
  // Get theme colors from settings
  const bgColor = settings.block_title_bg_color || '#f8f9fa';
  const textColor = settings.block_title_color || '#8B4513';

  // Add CSS styles directly to the document head
    useEffect(() => {
      const styleTag = document.createElement('style');
      styleTag.innerHTML = `
        /* Apply direct styles to these elements */
        .card-header.${themeClassName},
        .modal-header.${themeClassName} {
          background-color: ${bgColor} !important;
          color: ${textColor} !important;
          border-color: ${bgColor} !important;
        }
        
        /* Force all children to have the text color */
        .card-header.${themeClassName} *,
        .modal-header.${themeClassName} * {
          color: ${textColor} !important;
        }
        
        /* Override any other rules */
        .card .card-header.${themeClassName},
        .modal .modal-header.${themeClassName},
        .card-header.${themeClassName},
        .modal-header.${themeClassName},
        div.card-header.${themeClassName},
        div.modal-header.${themeClassName} {
          background-color: ${bgColor} !important;
          color: ${textColor} !important;
        }
        
        /* Direct style application */
        #root .card-header.${themeClassName},
        html body .card-header.${themeClassName} {
          background-color: ${bgColor} !important;
          color: ${textColor} !important;
        }
        
        /* For Description section specifically */
        .card-header.${themeClassName}[class*="theme-header"] {
          background-color: ${bgColor} !important;
          color: ${textColor} !important;
        }
      `;
      document.head.appendChild(styleTag);
      
      return () => {
        if (styleTag.parentNode) {
          document.head.removeChild(styleTag);
        }
      };
    }, [themeClassName, bgColor, textColor]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Validate phone
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (formData.phone.length !== 10) {
      errors.phone = 'Phone number must be exactly 10 digits';
      isValid = false;
    }

    // Validate password for new users
    if (!editMode && !formData.password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (!editMode && formData.password.trim().length < 6) {
      errors.password = 'Password must be at least 6 characters long';
      isValid = false;
    }

    // Validate role
    if (!formData.role.trim()) {
      errors.role = 'Role is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      // setPhoneError('');
      
      // Ensure organization_id is set to current user's organization
      const submitData = {
        ...formData,
        organization_id: currentUserOrg
      };
      
      if (editMode) {
        // Update existing user
        const updateData = {
          name: submitData.name.trim(),
          email: submitData.email.trim(),
          phone: submitData.phone.trim(),
          role: submitData.role,
          committee_role: submitData.committee_role,
          organization_id: currentUserOrg
        };
        
        // Only include password if it was provided and not empty
        if (submitData.password.trim()) {
          updateData.password = submitData.password.trim();
        }
        
        await userAPI.updateUser(submitData.id, updateData);
        
        // Update user in the list
        setUsers(users.map(user => 
          user.id === submitData.id ? 
          {...user, ...updateData, status: user.status || 'active'} : 
          user
        ));
        
        // If we're viewing this user's profile, update the selected user too
        if (selectedUser && selectedUser.id === submitData.id) {
          setSelectedUser({
            ...selectedUser,
            ...updateData
          });
        }
        
        toast.success(`User ${updateData.name} updated successfully!`);
        
      } else {
        // Create new user
        const newUserData = {
          ...submitData,
          name: submitData.name.trim(),
          email: submitData.email.trim(),
          phone: submitData.phone.trim(),
          password: submitData.password.trim(),
          role: submitData.role,
          committee_role: submitData.committee_role
        };
        
        const response = await userAPI.createUser(newUserData);
        
        // Add new user to list
        const newUser = {
          ...newUserData,
          id: response.data.userId,
          status: 'active',
          organization_id: currentUserOrg,
          created_at: new Date().toISOString()
        };
        
        setUsers([newUser, ...users]);
        
        toast.success(`${newUserData.role === 'guard' ? 'Guard' : 'User'} ${newUserData.name} created successfully!`);
      }
      
      // Reset form and errors
      resetForm();
      
      setError(null);
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Failed to save user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditUser = (user) => {
    // Close profile view if open
    // window.scrollTo(0, 0);
    setShowProfile(false);
    setFormErrors({});
    // setPhoneError(''); // Clear phone error
    setFormData({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      password: '', // Leave password empty when editing
      role: user.role,
      committee_role: user.committee_role,
      organization_id: user.organization_id || currentUserOrg
    });
    setEditMode(true);
    setShowForm(true);
    setError(null);
  };
  
  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setShowProfile(true);
    setShowForm(false); // Close form if open
    setError(null);
    // Fetch vehicles for this user
    vehicleAPI.getVehiclesByOwner(user.id)
      .then(res => setUserVehicles(res.data))
      .catch(err => console.error('Failed to load user vehicles:', err));
  };
  
  // Function to handle delete user button click
  const handleDeleteClick = (user) => {
    // Check if attempting to delete current user
    setError(null);
    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.id === currentUser.id) {
      setError('You cannot delete your own account.');
      return;
    }
    
    setUserToDelete(user);
    setShowDeleteModal(true);
  };
  
  // Function to handle user deletion
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setError(null);
      setDeleteLoading(true);
      await userAPI.deleteUser(userToDelete.id);
      
      // Remove user from the list
      setUsers(users.filter(user => user.id !== userToDelete.id));
      
      // Close the modal
      setShowDeleteModal(false);
      setUserToDelete(null);
      
      // Show success message
      toast.success(`User ${userToDelete.name} deleted successfully`);
      
      // If we're viewing this user's profile, close it
      if (selectedUser && selectedUser.id === userToDelete.id) {
        setShowProfile(false);
        setSelectedUser(null);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'owner',
      committee_role: '',
      organization_id: currentUserOrg
    });
    setFormErrors({
      name: '',
      email: '',
      password: '',
      phone: '',
      role: '',
      committee_role: ''
    });
    setShowForm(false);
    setEditMode(false);
    // setPhoneError('');
  };
  
  const closeProfile = () => {
    setShowProfile(false);
    setSelectedUser(null);
    setFormErrors({
      name: '',
      email: '',
      password: '',
      phone: '',
      role: '',
      committee_role: ''
    });
  };
  
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-danger';
      case 'guard':
        return 'bg-info';
      case 'owner':
        return 'bg-success';
      case 'maintenance':
        return 'bg-warning text-dark';
      default:
        return 'bg-secondary';
    }
  };
  
  // Function to handle user status toggle
  const handleStatusToggle = async (user) => {
    // Check if attempting to suspend own account
    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.id === currentUser.id) {
      setError('You cannot suspend your own account.');
      return;
    }
    
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    
    try {
      setLoading(true);
      await userAPI.updateUserStatus(user.id, newStatus);
      
      // Update user in the list
      setUsers(users.map(u => 
        u.id === user.id ? {...u, status: newStatus} : u
      ));
      
      // If we're viewing this user's profile, update the selected user too
      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser({
          ...selectedUser,
          status: newStatus
        });
      }
      
      // Show success message
      toast.success(`User ${user.name} is now ${newStatus}`);
      
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.response?.data?.message || `Failed to ${newStatus === 'active' ? 'activate' : 'suspend'} user. Please try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };
  
  if (loading && users.length === 0) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-title">
            <i className="fas fa-user-friends"></i> User Management
          </h1>
        </div>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle 
        icon="fa-user-friends" 
        title="User Management"
        action={
          <button 
            className="btn btn-primary" 
            onClick={() => {
              if (showForm && editMode) {
                resetForm();
              } else {
                setShowForm(!showForm);
                setShowProfile(false);
                setError(null);
                // setPhoneError('');
                // Reset form errors when toggling form
                setFormErrors({
                  name: '',
                  email: '',
                  password: '',
                  phone: '',
                  role: '',
                  committee_role: ''
                });
              }
            }}
          >
            {showForm ? 'Cancel' : 'Add New User'}
          </button>
        }
      />
      
      {/* User Profile View as Modal */}
      {showProfile && selectedUser && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1080 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div 
                  className={`modal-header ${themeClassName}`}
                  style={{
                    backgroundColor: bgColor,
                    color: textColor
                  }}
                >
                  <h5 className="modal-title">User Profile</h5>
                  <button type="button" className="btn-close" onClick={closeProfile}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">ID:</div>
                        <div className="col-md-8">#{selectedUser.id}</div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Name:</div>
                        <div className="col-md-8">{selectedUser.name}</div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Email:</div>
                        <div className="col-md-8">{selectedUser.email}</div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Phone:</div>
                        <div className="col-md-8">{selectedUser.phone}</div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Role:</div>
                        <div className="col-md-8">
                          <span className={`badge ${getRoleBadgeClass(selectedUser.role)}`}>
                            {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Status:</div>
                        <div className="col-md-8">
                          <span className={`badge ${selectedUser.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                            {selectedUser.status === 'active' ? 'Active' : 'Suspended'}
                          </span>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Organization:</div>
                        <div className="col-md-8">{ settings?.society_name || currentUser.organization_name || "-"}</div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4 fw-bold">Account Created:</div>
                        <div className="col-md-8">{formatDate(selectedUser.created_at)}</div>
                      </div>
                    </div>
                    <div className="col-md-4 text-center">
                      <div className="d-inline-block p-3 bg-light rounded-circle mb-3" style={{ width: '120px', height: '120px' }}>
                        <i className="fas fa-user-circle fa-5x text-secondary"></i>
                      </div>
                    </div>
                  </div>
                  {/* Vehicles list for this user */}
                  <div className="card mt-3">
                    <div className="card-header">
                      <h5 className="mb-0">Vehicles ({userVehicles.length})</h5>
                    </div>
                    <div className="card-body">
                      {userVehicles.length === 0 ? (
                        <div className="alert alert-info">No vehicles found for this user.</div>
                      ) : (
                        <table className="table table-sm">
                          <thead><tr><th>ID</th><th>Type</th><th>Reg. #</th><th>Make</th><th>Model</th><th>Color</th></tr></thead>
                          <tbody>{userVehicles.map(v => (
                            <tr key={v.id}><td>{v.id}</td><td>{v.vehicle_type}</td><td>{v.registration_number}</td><td>{v.make}</td><td>{v.model}</td><td>{v.color}</td></tr>
                          ))}</tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1070 }}></div>
        </>
      )}
      
      {/* User Form as Modal */}
      {showForm && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1080 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div 
                  className={`modal-header ${themeClassName}`}
                  style={{
                    backgroundColor: bgColor,
                    color: textColor
                  }}
                >
                  <h5 className="modal-title">{editMode ? 'Edit User' : 'Add New User'}</h5>
                  <button type="button" className="btn-close" onClick={resetForm}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}></button>
                </div>
                {error && (
                  <div className="alert alert-danger mx-3" role="alert">
                    {error}
                  </div>
                )}
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">Full Name</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      {formErrors.name && (
                        <div className="invalid-feedback">{formErrors.name}</div>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">Email Address</label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                      {formErrors.email && (
                        <div className="invalid-feedback">{formErrors.email}</div>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="phone" className="form-label">Phone Number</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={e => {
                          // Only allow digits
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData(prev => ({ ...prev, phone: value }));
                          setFormErrors(prev => ({ ...prev, phone: '' }));
                        }}
                        inputMode="numeric"
                        maxLength={10}
                        autoComplete="off"
                        title="Phone number must be exactly 10 digits"
                      />
                      {formErrors.phone && (
                        <div className="invalid-feedback">{formErrors.phone}</div>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">
                        {editMode ? 'Password (leave blank to keep current)' : 'Password'}
                      </label>
                      <input
                        type="password"
                        className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        minLength={!editMode ? "6" : ""}
                      />
                      {formErrors.password && (
                        <div className="invalid-feedback">{formErrors.password}</div>
                      )}
                      <div className="form-text">
                        {editMode 
                          ? 'Leave blank to keep the current password.' 
                          : 'Password must be at least 6 characters long.'}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="role" className="form-label">Role</label>
                      <select
                        className={`form-select ${formErrors.role ? 'is-invalid' : ''}`}
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        <option value="owner">Owner</option>
                        <option value="guard">Security Guard</option>
                        <option value="maintenance">Maintenance Personnel</option>
                        <option value="admin">Administrator</option>
                      </select>
                      {formErrors.role && (
                        <div className="invalid-feedback">{formErrors.role}</div>
                      )}
                    </div>
                    
                    {currentUser.role === 'admin' && (
                      <div className="mb-3">
                        <label htmlFor="committee_role" className="form-label">Committee Position</label>
                        <select
                          className="form-select"
                          id="committee_role"
                          name="committee_role"
                          value={formData.committee_role}
                          onChange={handleInputChange}
                        >
                          <option value="">None</option>
                          <option value="President">President</option>
                          <option value="Vice President">Vice President</option>
                          <option value="Secretary">Secretary</option>
                          <option value="Treasurer">Treasurer</option>
                          <option value="Governing Member">Governing Member</option>
                        </select>
                        <div className="form-text">Assign committee position (Admins only).</div>
                      </div>
                    )}
                    
                    {/* Hidden organization_id field - organization is automatically set to current user's organization */}
                    <input
                      type="hidden"
                      name="organization_id"
                      value={currentUserOrg || ''}
                    />
                    
                    <div className="d-flex justify-content-end">
                      <button 
                        type="button" 
                        className="btn btn-secondary me-2"
                        onClick={() => {
                          resetForm();
                          // Reset form errors when cancel button is clicked
                          setFormErrors({
                            name: '',
                            email: '',
                            password: '',
                            phone: '',
                            role: '',
                            committee_role: ''
                          });
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading 
                          ? (editMode ? 'Updating...' : 'Creating...') 
                          : (editMode ? 'Update User' : 'Create User')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1070 }}></div>
        </>
      )}
      
      {/* Search Bar */}
      <div className="card mb-4">
        <div className="card-body">
          <input
            type="text"
            className="form-control"
            placeholder="Search users by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="card" id="users-table-section">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">All Users</h5>
            <span className="badge bg-primary">{filteredUsers.length} Users Found</span>
          </div>
        </div>
        <div className="card-body">
          {filteredUsers.length === 0 ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              No users found matching your search.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(user => (
                    <tr key={user.id} className={user.status === 'suspended' ? 'table-secondary' : ''}>
                      <td>#{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td>
                        <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                          {user.status === 'active' ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="btn-group">
                          <button 
                            className="btn btn-sm btn-outline-secondary justify-content-center me-1"
                            onClick={() => handleViewProfile(user)}
                            title="View Profile"
                          >
                            <i className="fas fa-user"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-primary justify-content-center me-1"
                            onClick={() => handleEditUser(user)}
                            title="Edit User"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className={`btn btn-sm justify-content-center me-1 ${user.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            onClick={() => handleStatusToggle(user)}
                            title={user.status === 'active' ? 'Suspend User' : 'Activate User'}
                            disabled={user.id === JSON.parse(sessionStorage.getItem('user') || '{}').id}
                          >
                            {user.status === 'active' ? 'Suspend' : 'Activate'}
                          </button>
                          <button 
                            className="btn btn-sm justify-content-center btn-outline-danger"
                            onClick={() => handleDeleteClick(user)}
                            title="Delete User"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {filteredUsers.length > itemsPerPage && (
        <div className="card-footer d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} users
          </div>
          <nav aria-label="User pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                  <i className="fas fa-chevron-left"></i>
                </button>
              </li>
              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;
                return (
                  <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => paginate(pageNumber)}>
                      {pageNumber}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">Are you sure you want to delete user <strong>{userToDelete?.name}</strong>?</p>
                <p className="text-danger mb-0 mt-2"><i className="fas fa-exclamation-triangle me-2"></i>This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-alt me-2"></i>
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users; 